import { doGET, doPOST, doPUT } from "./HttpUtil";

export const DOC_TYPE_FORM_TYPES = {
  INDIVIDUAL_PLUS: "INDIVIDUAL_PLUS",
  BUSINESS_PLUS: "BUSINESS_PLUS",
  BUSINESS_PRIME: "BUSINESS_PRIME",
  BUSINESS_PRIME_UPGRADE: "BUSINESS_PRIME_UPGRADE",
  BUSINESS_V2_PLUS: "BUSINESS_V2_PLUS",
  BUSINESS_V2_PRIME: "BUSINESS_V2_PRIME",
  BUSINESS_V2_UPGRADE: "BUSINESS_V2_UPGRADE",
  INDIVIDUAL_PLUS_PR: "INDIVIDUAL_PLUS_PR",
  INDIVIDUAL_PRIME_PR: "INDIVIDUAL_PRIME_PR",
  BUSINESS_PLUS_PR: "BUSINESS_PLUS_PR",
  BUSINESS_PRIME_PR: "BUSINESS_PRIME_PR",
};

const buildRule = ({ formType, docTypes, andConditions = [], orConditions = [], name }) => ({
  formType,
  docTypes,
  andConditions,
  orConditions,
  status: 1,
  name: name || `${formType} - ${docTypes.join(", ")}`,
});

export const buildDefaultDocTypeRules = () => {
  const baseBusinessDocs = ["BusinessTaxIdProof", "Business_Proof_Address", "SourceOfBusFundsProof"];
  const individualSourceOfFundsConditions = [
    { fieldName: "individuals[].isShareHolder", operator: "EQ", values: "true" },
    { fieldName: "individuals[].percentOwner", operator: "GTE", values: "25" },
  ];
  const buildControlProngIndividualRules = (formType, label) => ([
    buildRule({
      formType,
      docTypes: ["ProofOfAddress"],
      andConditions: [{ fieldName: "individuals[].isControlPerson", operator: "EQ", values: "true" }],
      name: `${label} - Proof of Address (Control Prong)`,
    }),
  ]);
  const individualIdentificationProofRules = [
    {
      orConditions: [
        { fieldName: "individuals[].isDirector", operator: "EQ", values: "true" },
        { fieldName: "individuals[].isIndependentDirector", operator: "EQ", values: "true" },
      ],
      andConditions: [
        { fieldName: "individuals[].isAuthorizedUser", operator: "NE", values: "true" },
        { fieldName: "individuals[].percentOwner", operator: "LT", values: "25" },
      ],
    },
    {
      andConditions: [
        { fieldName: "individuals[].isDirector", operator: "EQ", values: "true" },
        { fieldName: "individuals[].isAuthorizedUser", operator: "NE", values: "true" },
        { fieldName: "individuals[].isShareHolder", operator: "NE", values: "true" },
      ],
    },
  ];

  const primeCoreDocs = [
    "shareholders",
    "principleProofOfAddress",
    "SourceOfBusFundsProof",
    "corporateStructureChart",
    "organizationalChart",
    "flowOfFundsDiagram",
  ];
  const v2PrimeCoreDocs = Array.from(new Set([...(baseBusinessDocs || []), ...(primeCoreDocs || [])]));

  const primePeriodicCoreDocs = ["shareholders", "corporateStructureChart", "organizationalChart", "flowOfFundsDiagram"];

  const conditionalPrimeDocs = [
    buildRule({
      formType: DOC_TYPE_FORM_TYPES.BUSINESS_PRIME,
      docTypes: ["businessPlan"],
      andConditions: [{ fieldName: "hasBusinessPlan", operator: "EQ", values: "true" }],
      name: "Business Prime - Business Plan",
    }),
    buildRule({
      formType: DOC_TYPE_FORM_TYPES.BUSINESS_PRIME,
      docTypes: ["license"],
      orConditions: [
        { fieldName: "hasMsbLicense", operator: "EQ", values: "true" },
        { fieldName: "hasLicense", operator: "EQ", values: "true" },
      ],
      name: "Business Prime - License",
    }),
    buildRule({
      formType: DOC_TYPE_FORM_TYPES.BUSINESS_PRIME,
      docTypes: ["amlProgram"],
      andConditions: [{ fieldName: "hasAmlProgram", operator: "EQ", values: "true" }],
      name: "Business Prime - AML Program",
    }),
    buildRule({
      formType: DOC_TYPE_FORM_TYPES.BUSINESS_PRIME,
      docTypes: ["amlReview"],
      andConditions: [{ fieldName: "amlCftReviewed", operator: "EQ", values: "true" }],
      name: "Business Prime - AML Review",
    }),
    buildRule({
      formType: DOC_TYPE_FORM_TYPES.BUSINESS_PRIME,
      docTypes: ["compliancePolicy"],
      andConditions: [{ fieldName: "hasComplaintsPolicy", operator: "EQ", values: "true" }],
      name: "Business Prime - Complaints Policy",
    }),
    buildRule({
      formType: DOC_TYPE_FORM_TYPES.BUSINESS_PRIME,
      docTypes: ["businessAgreement"],
      andConditions: [{ fieldName: "dealsInCryptocurrency", operator: "EQ", values: "true" }],
      name: "Business Prime - Business Agreement",
    }),
  ];

  const conditionalPrimeUpgradeDocs = conditionalPrimeDocs.map((rule) => ({
    ...rule,
    formType: DOC_TYPE_FORM_TYPES.BUSINESS_PRIME_UPGRADE,
    name: rule.name?.replace("Business Prime", "Business Prime Upgrade"),
  }));

  const conditionalPrimePeriodicDocs = [
    buildRule({
      formType: DOC_TYPE_FORM_TYPES.BUSINESS_PRIME_PR,
      docTypes: ["license"],
      orConditions: [
        { fieldName: "hasMsbLicense", operator: "EQ", values: "true" },
        { fieldName: "hasLicense", operator: "EQ", values: "true" },
      ],
      name: "Business Prime PR - License",
    }),
    buildRule({
      formType: DOC_TYPE_FORM_TYPES.BUSINESS_PRIME_PR,
      docTypes: ["amlProgram"],
      andConditions: [{ fieldName: "hasAmlProgram", operator: "EQ", values: "true" }],
      name: "Business Prime PR - AML Program",
    }),
    buildRule({
      formType: DOC_TYPE_FORM_TYPES.BUSINESS_PRIME_PR,
      docTypes: ["amlReview"],
      andConditions: [{ fieldName: "amlCftReviewed", operator: "EQ", values: "true" }],
      name: "Business Prime PR - AML Review",
    }),
    buildRule({
      formType: DOC_TYPE_FORM_TYPES.BUSINESS_PRIME_PR,
      docTypes: ["compliancePolicy"],
      andConditions: [{ fieldName: "hasComplaintsPolicy", operator: "EQ", values: "true" }],
      name: "Business Prime PR - Complaints Policy",
    }),
    buildRule({
      formType: DOC_TYPE_FORM_TYPES.BUSINESS_PRIME_PR,
      docTypes: ["businessAgreement"],
      andConditions: [{ fieldName: "crypto", operator: "EQ", values: "true" }],
      name: "Business Prime PR - Business Agreement",
    }),
  ];

  return [
    buildRule({
      formType: DOC_TYPE_FORM_TYPES.INDIVIDUAL_PLUS,
      docTypes: ["ProofOfAddress"],
      name: "Individual Plus - Proof of Address",
    }),
    buildRule({
      formType: DOC_TYPE_FORM_TYPES.BUSINESS_PLUS,
      docTypes: baseBusinessDocs,
      name: "Business Plus - Core Business Documents",
    }),
    buildRule({
      formType: DOC_TYPE_FORM_TYPES.BUSINESS_PLUS,
      docTypes: ["ProofOfAddress"],
      andConditions: [{ fieldName: "individuals[].isAuthorizedUser", operator: "EQ", values: "true" }],
      name: "Business Plus - Proof of Address (Authorized User)",
    }),
    buildRule({
      formType: DOC_TYPE_FORM_TYPES.BUSINESS_PLUS,
      docTypes: ["ProofOfAddress"],
      andConditions: [
        { fieldName: "individuals[].isShareHolder", operator: "EQ", values: "true" },
        { fieldName: "individuals[].percentOwner", operator: "GTE", values: "10" },
      ],
      name: "Business Plus - Proof of Address (Shareholder >=10%)",
    }),
    buildRule({
      formType: DOC_TYPE_FORM_TYPES.BUSINESS_PLUS,
      docTypes: ["ProofOfAddress"],
      andConditions: [{ fieldName: "individuals[].isDirector", operator: "EQ", values: "true" }],
      name: "Business Plus - Proof of Address (Director)",
    }),
    buildRule({
      formType: DOC_TYPE_FORM_TYPES.BUSINESS_PLUS,
      docTypes: ["ProofOfAddress"],
      andConditions: [{ fieldName: "individuals[].isIndependentDirector", operator: "EQ", values: "true" }],
      name: "Business Plus - Proof of Address (Independent Director)",
    }),
    buildRule({
      formType: DOC_TYPE_FORM_TYPES.BUSINESS_PLUS,
      docTypes: ["SourceOfFunds"],
      andConditions: individualSourceOfFundsConditions,
      name: "Business Plus - Individual Source of Funds",
    }),
    ...individualIdentificationProofRules.map((rule, idx) =>
      buildRule({
        formType: DOC_TYPE_FORM_TYPES.BUSINESS_PLUS,
        docTypes: ["IdentificationProof"],
        andConditions: rule.andConditions,
        orConditions: rule.orConditions,
        name: `Business Plus - Identification Proof ${idx + 1}`,
      }),
    ),
    buildRule({
      formType: DOC_TYPE_FORM_TYPES.BUSINESS_V2_PLUS,
      docTypes: baseBusinessDocs,
      name: "Business V2 Plus - Core Business Documents",
    }),
    ...buildControlProngIndividualRules(DOC_TYPE_FORM_TYPES.BUSINESS_V2_PLUS, "Business V2 Plus"),
    buildRule({
      formType: DOC_TYPE_FORM_TYPES.BUSINESS_V2_PLUS,
      docTypes: ["ProofOfAddress"],
      andConditions: [{ fieldName: "individuals[].isAuthorizedUser", operator: "EQ", values: "true" }],
      name: "Business V2 Plus - Proof of Address (Authorized User)",
    }),
    buildRule({
      formType: DOC_TYPE_FORM_TYPES.BUSINESS_V2_PLUS,
      docTypes: ["ProofOfAddress"],
      andConditions: [
        { fieldName: "individuals[].isShareHolder", operator: "EQ", values: "true" },
        { fieldName: "individuals[].percentOwner", operator: "GTE", values: "10" },
      ],
      name: "Business V2 Plus - Proof of Address (Shareholder >=10%)",
    }),
    buildRule({
      formType: DOC_TYPE_FORM_TYPES.BUSINESS_V2_PLUS,
      docTypes: ["ProofOfAddress"],
      andConditions: [{ fieldName: "individuals[].isDirector", operator: "EQ", values: "true" }],
      name: "Business V2 Plus - Proof of Address (Director)",
    }),
    buildRule({
      formType: DOC_TYPE_FORM_TYPES.BUSINESS_V2_PLUS,
      docTypes: ["ProofOfAddress"],
      andConditions: [{ fieldName: "individuals[].isIndependentDirector", operator: "EQ", values: "true" }],
      name: "Business V2 Plus - Proof of Address (Independent Director)",
    }),
    buildRule({
      formType: DOC_TYPE_FORM_TYPES.BUSINESS_V2_PLUS,
      docTypes: ["SourceOfFunds"],
      andConditions: individualSourceOfFundsConditions,
      name: "Business V2 Plus - Individual Source of Funds",
    }),
    ...individualIdentificationProofRules.map((rule, idx) =>
      buildRule({
        formType: DOC_TYPE_FORM_TYPES.BUSINESS_V2_PLUS,
        docTypes: ["IdentificationProof"],
        andConditions: rule.andConditions,
        orConditions: rule.orConditions,
        name: `Business V2 Plus - Identification Proof ${idx + 1}`,
      }),
    ),
    buildRule({
      formType: DOC_TYPE_FORM_TYPES.BUSINESS_PRIME,
      docTypes: primeCoreDocs,
      name: "Business Prime (Connected) - Core Documents",
    }),
    buildRule({
      formType: DOC_TYPE_FORM_TYPES.BUSINESS_PRIME_UPGRADE,
      docTypes: primeCoreDocs,
      name: "Business Prime Upgrade - Core Documents",
    }),
    buildRule({
      formType: DOC_TYPE_FORM_TYPES.BUSINESS_V2_PRIME,
      docTypes: v2PrimeCoreDocs,
      name: "Business V2 Prime - Core Documents",
    }),
    buildRule({
      formType: DOC_TYPE_FORM_TYPES.BUSINESS_V2_UPGRADE,
      docTypes: primeCoreDocs,
      name: "Business V2 Prime Upgrade - Core Documents",
    }),
    ...buildControlProngIndividualRules(DOC_TYPE_FORM_TYPES.BUSINESS_V2_PRIME, "Business V2 Prime"),
    ...buildControlProngIndividualRules(DOC_TYPE_FORM_TYPES.BUSINESS_V2_UPGRADE, "Business V2 Prime Upgrade"),
    ...conditionalPrimeDocs,
    ...conditionalPrimeUpgradeDocs,
    ...conditionalPrimeDocs.map((rule) => ({
      ...rule,
      formType: DOC_TYPE_FORM_TYPES.BUSINESS_V2_PRIME,
      name: rule.name?.replace("Business Prime", "Business V2 Prime"),
    })),
    buildRule({
      formType: DOC_TYPE_FORM_TYPES.BUSINESS_V2_PRIME,
      docTypes: ["ProofOfAddress"],
      andConditions: [{ fieldName: "individuals[].isAuthorizedUser", operator: "EQ", values: "true" }],
      name: "Business V2 Prime - Proof of Address (Authorized User)",
    }),
    buildRule({
      formType: DOC_TYPE_FORM_TYPES.BUSINESS_V2_PRIME,
      docTypes: ["ProofOfAddress"],
      andConditions: [
        { fieldName: "individuals[].isShareHolder", operator: "EQ", values: "true" },
        { fieldName: "individuals[].percentOwner", operator: "GTE", values: "10" },
      ],
      name: "Business V2 Prime - Proof of Address (Shareholder >=10%)",
    }),
    buildRule({
      formType: DOC_TYPE_FORM_TYPES.BUSINESS_V2_PRIME,
      docTypes: ["ProofOfAddress"],
      andConditions: [{ fieldName: "individuals[].isDirector", operator: "EQ", values: "true" }],
      name: "Business V2 Prime - Proof of Address (Director)",
    }),
    buildRule({
      formType: DOC_TYPE_FORM_TYPES.BUSINESS_V2_PRIME,
      docTypes: ["ProofOfAddress"],
      andConditions: [{ fieldName: "individuals[].isIndependentDirector", operator: "EQ", values: "true" }],
      name: "Business V2 Prime - Proof of Address (Independent Director)",
    }),
    buildRule({
      formType: DOC_TYPE_FORM_TYPES.BUSINESS_V2_PRIME,
      docTypes: ["SourceOfFunds"],
      andConditions: individualSourceOfFundsConditions,
      name: "Business V2 Prime - Individual Source of Funds",
    }),
    ...individualIdentificationProofRules.map((rule, idx) =>
      buildRule({
        formType: DOC_TYPE_FORM_TYPES.BUSINESS_V2_PRIME,
        docTypes: ["IdentificationProof"],
        andConditions: rule.andConditions,
        orConditions: rule.orConditions,
        name: `Business V2 Prime - Identification Proof ${idx + 1}`,
      }),
    ),
    ...conditionalPrimeUpgradeDocs.map((rule) => ({
      ...rule,
      formType: DOC_TYPE_FORM_TYPES.BUSINESS_V2_UPGRADE,
      name: rule.name?.replace("Business Prime Upgrade", "Business V2 Prime Upgrade"),
    })),
    // buildRule({
    //   formType: DOC_TYPE_FORM_TYPES.INDIVIDUAL_PLUS_PR,
    //   docTypes: ["ProofOfAddress"],
    //   name: "Individual Plus PR - Proof of Address",
    // }),
    // buildRule({
    //   formType: DOC_TYPE_FORM_TYPES.INDIVIDUAL_PRIME_PR,
    //   docTypes: ["ProofOfAddress"],
    //   name: "Individual Prime PR - Proof of Address",
    // }),
    // buildRule({
    //   formType: DOC_TYPE_FORM_TYPES.BUSINESS_PLUS_PR,
    //   docTypes: baseBusinessDocs,
    //   name: "Business Plus PR - Core Business Documents",
    // }),
    // buildRule({
    //   formType: DOC_TYPE_FORM_TYPES.BUSINESS_PLUS_PR,
    //   docTypes: ["ProofOfAddress"],
    //   andConditions: [{ fieldName: "individuals[].isAuthorizedUser", operator: "EQ", values: "true" }],
    //   name: "Business Plus PR - Proof of Address (Authorized User)",
    // }),
    // buildRule({
    //   formType: DOC_TYPE_FORM_TYPES.BUSINESS_PLUS_PR,
    //   docTypes: ["ProofOfAddress"],
    //   andConditions: [
    //     { fieldName: "individuals[].isShareHolder", operator: "EQ", values: "true" },
    //     { fieldName: "individuals[].percentOwner", operator: "GTE", values: "10" },
    //   ],
    //   name: "Business Plus PR - Proof of Address (Shareholder >=10%)",
    // }),
    // buildRule({
    //   formType: DOC_TYPE_FORM_TYPES.BUSINESS_PLUS_PR,
    //   docTypes: ["ProofOfAddress"],
    //   andConditions: [{ fieldName: "individuals[].isDirector", operator: "EQ", values: "true" }],
    //   name: "Business Plus PR - Proof of Address (Director)",
    // }),
    // buildRule({
    //   formType: DOC_TYPE_FORM_TYPES.BUSINESS_PLUS_PR,
    //   docTypes: ["ProofOfAddress"],
    //   andConditions: [{ fieldName: "individuals[].isIndependentDirector", operator: "EQ", values: "true" }],
    //   name: "Business Plus PR - Proof of Address (Independent Director)",
    // }),
    // buildRule({
    //   formType: DOC_TYPE_FORM_TYPES.BUSINESS_PLUS_PR,
    //   docTypes: ["SourceOfFunds"],
    //   andConditions: individualSourceOfFundsConditions,
    //   name: "Business Plus PR - Individual Source of Funds",
    // }),
    // ...individualIdentificationProofRules.map((rule, idx) =>
    //   buildRule({
    //     formType: DOC_TYPE_FORM_TYPES.BUSINESS_PLUS_PR,
    //     docTypes: ["IdentificationProof"],
    //     andConditions: rule.andConditions,
    //     orConditions: rule.orConditions,
    //     name: `Business Plus PR - Identification Proof ${idx + 1}`,
    //   }),
    // ),
    buildRule({
      formType: DOC_TYPE_FORM_TYPES.BUSINESS_PRIME_PR,
      docTypes: primePeriodicCoreDocs,
      name: "Business Prime PR - Core Documents",
    }),
    ...buildControlProngIndividualRules(DOC_TYPE_FORM_TYPES.BUSINESS_PLUS_PR, "Business Plus PR"),
    ...buildControlProngIndividualRules(DOC_TYPE_FORM_TYPES.BUSINESS_PRIME_PR, "Business Prime PR"),
    ...conditionalPrimePeriodicDocs,
  ];
};

const DEFAULT_DOC_TYPE_META = {
  ProofOfAddress: {
    label: "Upload a proof of address for {displayName}",
    errorMsg: "Please upload a proof of address for {displayName}.",
    fileExtensions: ["pdf", "image"],
  },
  Business_Proof_Address: {
    label:
      "Upload a proof of address for the principal place of business (operating address). (Rental/lease agreement, Utility bill or bank statement issued within 90 days)",
    errorMsg:
      "Upload a proof of address for the principal place of business (operating address). (Rental/lease agreement, Utility bill or bank statement issued within 90 days)",
    fileExtensions: ["pdf", "image"],
  },
  BusinessTaxIdProof: {
    label: "Upload proof of Business Tax ID (If USA incorporated , please upload proof of EIN.)",
    errorMsg: "Upload proof of Business Tax ID (If USA incorporated , please upload proof of EIN.)",
  },
  SourceOfBusFundsProof: {
    label: "Upload a source of funds for the business (i.e. bank statement, trading account statement, invoice or contract.)",
    errorMsg: "Upload a source of funds for the business (i.e. bank statement, trading account statement, invoice or contract.)",
  },
  SourceOfFunds: {
    label: "Upload proof of source of funds for {displayName}.",
    errorMsg: "Upload proof of source of funds for {displayName}.",
  },
  IdentificationProof: {
    label: "Upload identification proof for {displayName}.",
    errorMsg: "Upload identification proof for {displayName}.",
  },
  shareholders: {
    label: "Please upload a register of shareholders, members or partners.",
    errorMsg: "Please upload a register of shareholders, members or partners.",
  },
  principleProofOfAddress: {
    label:
      "Please upload a proof of address for the principal place of business(operating address). (Utility bill or bank statement issued with in the last 90 days, rental contract or lease agreement)",
    errorMsg:
      "Please upload a proof of address for the principal place of business(operating address). (Utility bill or bank statement issued with in the last 90 days, rental contract or lease agreement)",
  },
  corporateStructureChart: {
    label:
      "Please provide a corporate structure chart which shows name, country of formation, year of incorporation and nature of business of any parent, subsidiary and/or affiliate",
    errorMsg:
      "Please provide a corporate structure chart which shows name, country of formation, year of incorporation and nature of business of any parent, subsidiary and/or affiliate",
  },
  organizationalChart: {
    label: "Please provide the organizational chart (Organogram)",
    errorMsg: "Please provide the organizational chart (Organogram)",
  },
  license: {
    label: "Please upload a copy of your license or exemption.",
    errorMsg: "Please upload a copy of your license or exemption.",
  },
  amlProgram: {
    label: "Please upload your AML/CFT Program (policies and procedures).",
    errorMsg: "Please upload your AML/CFT Program (policies and procedures).",
  },
  amlReview: {
    label: "Please upload your independent AML/CFT review.",
    errorMsg: "Please upload your independent AML/CFT review.",
  },
  compliancePolicy: {
    label: "Please upload your complaints policy.",
    errorMsg: "Please upload your complaints policy.",
  },
  flowOfFundsDiagram: {
    label: "Please upload a flow of funds diagram that integrates the role of FV Bank.",
    errorMsg: "Please upload a flow of funds diagram that integrates the role of FV Bank.",
  },
  businessAgreement: {
    label: "Please upload your business agreement or terms and conditions.",
    errorMsg: "Please upload your business agreement or terms and conditions.",
  },
  businessPlan: {
    label: "Please upload your business plan.",
    errorMsg: "Please upload your business plan.",
  },
};

export const updateDocTypesByKey = async (metaUpdates = DEFAULT_DOC_TYPE_META) => {
  const results = [];
  try {
    const response = await doGET(`/api/doc-type/grid?rows=-1`);
    const rows = response?.data?.rows || [];
    const rowByKey = rows.reduce((acc, row) => {
      if (row?.key) acc[row.key] = row;
      if (row?.name && !acc[row.name]) acc[row.name] = row;
      return acc;
    }, {});

    for (const [key, meta] of Object.entries(metaUpdates || {})) {
      const existing = rowByKey[key];
      if (!existing?._id) {
        results.push({ success: false, key, error: "Doc type not found" });
        continue;
      }
      const payload = {
        ...existing,
        label: meta?.label ?? existing?.label,
        errorMsg: meta?.errorMsg ?? existing?.errorMsg,
        fileExtensions: meta?.fileExtensions ?? existing?.fileExtensions,
      };
      try {
        const res = await doPUT(`/api/doc-type/update`, payload);
        results.push({ success: true, key, response: res?.data });
      } catch (err) {
        results.push({ success: false, key, error: err?.message || "Failed to update doc type" });
      }
    }
  } catch (error) {
    return [{ success: false, error: error?.message || "Failed to fetch doc types" }];
  }

  return results;
};

export const updateDefaultDocTypeRulesForForms = async () => {
  const results = [];
  const rules = buildDefaultDocTypeRules();

  for (const rule of rules) {
    try {
      const response = await doPOST("/api/doc-type-rule/create", rule);
      results.push({ success: true, rule, response: response?.data });
    } catch (error) {
      results.push({
        success: false,
        rule,
        error: error?.message || "Failed to create rule",
      });
    }
  }

  return results;
};
